import React from "react";
import { SimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import { Button } from "@/components/ui/button";
import { Localizer } from "@/main/react/Types";
import { LocalizationKey } from "@/main/util/LocalizationUtil";
import { ShareEntityType, ShareMomentKind, shareUrl } from "@/main/util/PlatformIdentifierUtil";

export interface ShareMomentDialogProps {
    kind: ShareMomentKind;
    entityType: ShareEntityType;
    /** publicId — the SHARE_INITIATED join key. */
    entityId: string;
    /** Absolute and TOKEN-FREE. The chokepoint appends `?sv_s`; pre-tokenizing here would double it. */
    url: string;
    /** The post's own title. Doubles as the dialog's accessible name. */
    title: string;
    authorDisplayName?: string;
    /** `/og/discussion/{publicId}.png`. Omitted → the preview slot is not rendered at all. */
    previewImageUrl?: string;
    localizer: Localizer;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const BODY_KEY: Record<ShareMomentKind, LocalizationKey> = {
    post_publish: "share.moment.post_publish.body",
    first_response: "share.moment.first_response.body",
    // Tier-2 finding 22. A separate kind rather than a conditional inside `post_publish`, because the
    // `Record<ShareMomentKind, …>` above is what forces a new kind to bring its own copy — the type
    // system doing the job `PlatformIdentifierUtil`'s note describes.
    first_post: "share.moment.first_post.body",
    // The three pride moments (round-26 D5 / D11). Each says one fact and makes one ask, the same shape
    // as the two live moments above — no count, no superlative the member has to defend.
    answer_accepted: "share.moment.answer_accepted.body",
    top_reply: "share.moment.top_reply.body",
    gaining_traction: "share.moment.gaining_traction.body",
};

/**
 * The share moment: one preview, one sentence, one action.
 *
 * <p><strong>It never calls `navigator.share` itself.</strong> The primary action goes through
 * {@link shareUrl}, and that is the single load-bearing rule of the whole feature — it is what keeps token
 * minting, `SHARE_INITIATED` emission, the `AbortError` policy, the payload composition and the toast copy
 * in exactly one place, and what makes every future improvement to the chokepoint (the desktop drawer, a
 * richer payload) arrive here for free. A jest spy asserts `navigator.share` is never touched directly;
 * that assertion is the one that must never be deleted.
 *
 * <p><strong>One action, not a channel grid.</strong> The native sheet beat a channel-button grid by ~20%
 * in the strongest measurement available (Google Santa Tracker), so the moment presents a single control
 * that opens the OS sheet. The desktop degradation of that control is the chokepoint's problem, not a
 * second button here.
 *
 * <p><strong>No share count, no email capture, no copy-link row.</strong> Low counts are negative social
 * proof; the rest is scope that would turn a nudge into a feature.
 */
export function ShareMomentDialog(props: ShareMomentDialogProps) {
    const { localizer } = props;

    const handleShare = async () => {
        const shared = await shareUrl(props.url, props.title, localizer, {
            entityType: props.entityType,
            entityId: props.entityId,
            entityTitle: props.title,
            authorDisplayName: props.authorDisplayName,
            moment: props.kind,
        });
        // `false` means the author dismissed the OS sheet (AbortError). They dismissed the SHEET, not the
        // moment, and re-presenting it is one tap away — so stay open, silently, with no error state. The
        // chokepoint has already guaranteed nothing was emitted either way.
        if (shared) {
            props.onOpenChange(false);
        }
    };

    return (
        <SimpleDialog
            width="md"
            open={props.open}
            onOpenChange={props.onOpenChange}
            title={props.title}
            description={localizer(BODY_KEY[props.kind])}
            mobilePresentation="sheet"
        >
            <div className="flex flex-col gap-4" data-playwright-id="share-moment">
                {props.previewImageUrl ? (
                    <img
                        src={props.previewImageUrl}
                        // Decorative: the post title is already the dialog's accessible name, so a
                        // non-empty alt would announce it twice to a screen reader.
                        alt=""
                        loading="lazy"
                        className="w-full max-w-full rounded-md border border-border aspect-[1200/630] object-cover"
                        data-playwright-id="share-moment-preview"
                    />
                ) : null}

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                        variant="ghost"
                        onClick={() => props.onOpenChange(false)}
                        data-playwright-id="share-moment-dismiss"
                    >
                        {localizer("notifications.private_access_blocked.dialog.not_now")}
                    </Button>
                    <Button
                        autoFocus
                        onClick={() => void handleShare()}
                        data-playwright-id="share-moment-share"
                    >
                        {localizer("share.moment.first_response.action")}
                    </Button>
                </div>
            </div>
        </SimpleDialog>
    );
}

export default ShareMomentDialog;
